import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Mic,
  Video,
  ArrowRight,
  ArrowLeft,
  Headphones,
  Loader2,
  Target,
  Users,
  DollarSign,
  Calendar
} from "lucide-react";

const BUDGET_OPTIONS = [
  { value: "starter", label: "Starter ($50-150)", desc: "Basic equipment to get started" },
  { value: "mid", label: "Mid-Range ($300-600)", desc: "Professional quality setup" },
  { value: "pro", label: "Professional ($1000+)", desc: "Studio-grade equipment" }
];

const FREQUENCY_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "daily", label: "Daily" }
];

const EXPERIENCE_OPTIONS = [
  { value: "beginner", label: "Complete Beginner", desc: "Never created a podcast before" },
  { value: "some", label: "Some Experience", desc: "Recorded some content but not launched" },
  { value: "experienced", label: "Experienced", desc: "Have an existing podcast to improve" }
];

const TOPIC_SUGGESTIONS = [
  "Business & Entrepreneurship",
  "Personal Development",
  "Technology & Innovation",
  "Health & Wellness",
  "Entertainment & Pop Culture",
  "Education & Learning",
  "True Crime & Mystery",
  "Sports & Fitness",
  "Science & Nature",
  "Arts & Creativity",
  "Finance & Investing",
  "Relationships & Dating",
  "Comedy & Humor",
  "News & Politics",
  "Spirituality & Religion"
];

export function PodcastIntakePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [checkingEnrollment, setCheckingEnrollment] = useState(true);
  
  const [formData, setFormData] = useState({
    podcast_type: "",
    podcast_name: "",
    podcast_description: "",
    target_audience: "",
    goals: "",
    experience_level: "beginner",
    budget: "starter",
    frequency: "weekly",
    topics_of_interest: [],
    has_equipment: false,
    current_equipment: ""
  });

  useEffect(() => {
    checkEnrollment();
  }, []);

  const checkEnrollment = async () => {
    try {
      const response = await authFetch(`${API}/podcast/enrollment`);
      if (response.ok) {
        const data = await response.json();
        if (data && data.status !== "intake_pending") {
          // Already completed intake, redirect to dashboard
          navigate("/podcast");
          return;
        }
      }
    } catch (_error) {
      console.error("Error checking enrollment:", _error);
    } finally {
      setCheckingEnrollment(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleTopic = (topic) => {
    setFormData(prev => ({
      ...prev,
      topics_of_interest: prev.topics_of_interest.includes(topic)
        ? prev.topics_of_interest.filter(t => t !== topic)
        : [...prev.topics_of_interest, topic]
    }));
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        return !!formData.podcast_type;
      case 2:
        return formData.podcast_name.trim() && formData.target_audience.trim();
      case 3:
        return formData.goals.trim() && formData.topics_of_interest.length > 0;
      case 4:
        return true; // Optional fields
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/podcast/intake`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success("Intake submitted! Let's start Week 1!");
        navigate("/podcast");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to submit intake");
      }
    } catch (_error) {
      toast.error("Failed to submit intake");
    } finally {
      setLoading(false);
    }
  };

  if (checkingEnrollment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-purple-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Mic className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-neutral-900">Podcast Launch Academy</h1>
              <p className="text-xs text-neutral-500">Intake Form - Step {step} of 4</p>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-neutral-100">
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={`flex-1 h-1 ${s <= step ? "bg-gradient-to-r from-purple-500 to-pink-500" : "bg-neutral-200"}`}
              />
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Step 1: Podcast Type */}
        {step === 1 && (
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">What type of podcast are you creating?</CardTitle>
              <CardDescription>
                This helps us customize equipment recommendations and production tips
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-4">
                <button
                  onClick={() => handleChange("podcast_type", "audio")}
                  className={`p-6 rounded-xl border-2 text-left transition-all ${
                    formData.podcast_type === "audio"
                      ? "border-purple-500 bg-purple-50"
                      : "border-neutral-200 hover:border-purple-300"
                  }`}
                  data-testid="podcast-type-audio"
                >
                  <Headphones className={`w-10 h-10 mb-3 ${formData.podcast_type === "audio" ? "text-purple-600" : "text-neutral-400"}`} />
                  <h3 className="font-bold text-lg mb-1">Audio Podcast</h3>
                  <p className="text-sm text-neutral-600">
                    Focus on audio recording and editing. Distributed on Spotify, Apple Podcasts, etc.
                  </p>
                </button>

                <button
                  onClick={() => handleChange("podcast_type", "video")}
                  className={`p-6 rounded-xl border-2 text-left transition-all ${
                    formData.podcast_type === "video"
                      ? "border-pink-500 bg-pink-50"
                      : "border-neutral-200 hover:border-pink-300"
                  }`}
                  data-testid="podcast-type-video"
                >
                  <Video className={`w-10 h-10 mb-3 ${formData.podcast_type === "video" ? "text-pink-600" : "text-neutral-400"}`} />
                  <h3 className="font-bold text-lg mb-1">Video Podcast</h3>
                  <p className="text-sm text-neutral-600">
                    Audio + video recording. Distributed on YouTube and audio platforms.
                  </p>
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Podcast Details */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-purple-500" />
                Tell us about your podcast
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="podcast_name">Podcast Name *</Label>
                <Input
                  id="podcast_name"
                  placeholder="e.g., The Entrepreneur's Journey"
                  value={formData.podcast_name}
                  onChange={(e) => handleChange("podcast_name", e.target.value)}
                  data-testid="podcast-name-input"
                />
                <p className="text-xs text-neutral-500">Don't worry, you can change this later</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="podcast_description">Podcast Description</Label>
                <Textarea
                  id="podcast_description"
                  placeholder="What is your podcast about? What makes it unique?"
                  value={formData.podcast_description}
                  onChange={(e) => handleChange("podcast_description", e.target.value)}
                  rows={3}
                  data-testid="podcast-description-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="target_audience">Who is your target audience? *</Label>
                <Textarea
                  id="target_audience"
                  placeholder="e.g., Small business owners aged 30-50 looking to scale their operations"
                  value={formData.target_audience}
                  onChange={(e) => handleChange("target_audience", e.target.value)}
                  rows={2}
                  data-testid="target-audience-input"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Goals & Topics */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-500" />
                Goals & Content
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="goals">What are your goals for this podcast? *</Label>
                <Textarea
                  id="goals"
                  placeholder="e.g., Build thought leadership, generate leads for my business, create a community..."
                  value={formData.goals}
                  onChange={(e) => handleChange("goals", e.target.value)}
                  rows={3}
                  data-testid="goals-input"
                />
              </div>

              <div className="space-y-3">
                <Label>Topics of Interest (Select at least 1) *</Label>
                <div className="flex flex-wrap gap-2">
                  {TOPIC_SUGGESTIONS.map((topic) => (
                    <Badge
                      key={topic}
                      variant={formData.topics_of_interest.includes(topic) ? "default" : "outline"}
                      className={`cursor-pointer transition-colors ${
                        formData.topics_of_interest.includes(topic)
                          ? "bg-purple-500 hover:bg-purple-600"
                          : "hover:bg-purple-50"
                      }`}
                      onClick={() => toggleTopic(topic)}
                      data-testid={`topic-${topic.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      {topic}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <Label>Planned Release Frequency</Label>
                <RadioGroup
                  value={formData.frequency}
                  onValueChange={(v) => handleChange("frequency", v)}
                  className="grid grid-cols-2 gap-2"
                >
                  {FREQUENCY_OPTIONS.map((opt) => (
                    <div key={opt.value} className="flex items-center space-x-2">
                      <RadioGroupItem value={opt.value} id={`freq-${opt.value}`} />
                      <Label htmlFor={`freq-${opt.value}`} className="cursor-pointer">
                        {opt.label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Experience & Equipment */}
        {step === 4 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-purple-500" />
                Experience & Budget
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label>Your Experience Level</Label>
                <RadioGroup
                  value={formData.experience_level}
                  onValueChange={(v) => handleChange("experience_level", v)}
                  className="space-y-2"
                >
                  {EXPERIENCE_OPTIONS.map((opt) => (
                    <div
                      key={opt.value}
                      className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        formData.experience_level === opt.value
                          ? "border-purple-300 bg-purple-50"
                          : "border-neutral-200 hover:bg-neutral-50"
                      }`}
                      onClick={() => handleChange("experience_level", opt.value)}
                    >
                      <RadioGroupItem value={opt.value} id={`exp-${opt.value}`} className="mt-0.5" />
                      <div>
                        <Label htmlFor={`exp-${opt.value}`} className="font-medium cursor-pointer">
                          {opt.label}
                        </Label>
                        <p className="text-sm text-neutral-500">{opt.desc}</p>
                      </div>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              <div className="space-y-3">
                <Label>Equipment Budget</Label>
                <RadioGroup
                  value={formData.budget}
                  onValueChange={(v) => handleChange("budget", v)}
                  className="space-y-2"
                >
                  {BUDGET_OPTIONS.map((opt) => (
                    <div
                      key={opt.value}
                      className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        formData.budget === opt.value
                          ? "border-purple-300 bg-purple-50"
                          : "border-neutral-200 hover:bg-neutral-50"
                      }`}
                      onClick={() => handleChange("budget", opt.value)}
                    >
                      <RadioGroupItem value={opt.value} id={`budget-${opt.value}`} className="mt-0.5" />
                      <div>
                        <Label htmlFor={`budget-${opt.value}`} className="font-medium cursor-pointer">
                          {opt.label}
                        </Label>
                        <p className="text-sm text-neutral-500">{opt.desc}</p>
                      </div>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="has_equipment"
                    checked={formData.has_equipment}
                    onCheckedChange={(v) => handleChange("has_equipment", v)}
                  />
                  <Label htmlFor="has_equipment" className="cursor-pointer">
                    I already have some podcasting equipment
                  </Label>
                </div>

                {formData.has_equipment && (
                  <div className="space-y-2 pl-6">
                    <Label htmlFor="current_equipment">What equipment do you have?</Label>
                    <Textarea
                      id="current_equipment"
                      placeholder="e.g., Blue Yeti microphone, Sony headphones..."
                      value={formData.current_equipment}
                      onChange={(e) => handleChange("current_equipment", e.target.value)}
                      rows={2}
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={() => step === 1 ? navigate("/podcast") : setStep(step - 1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {step === 1 ? "Cancel" : "Back"}
          </Button>

          {step < 4 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={!validateStep()}
              className="bg-gradient-to-r from-purple-600 to-pink-600"
              data-testid="next-btn"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-gradient-to-r from-purple-600 to-pink-600"
              data-testid="submit-intake-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Start Week 1
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}

export default PodcastIntakePage;
